import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Activity, TrendingUp, TrendingDown, Users, AlertTriangle, BarChart3,
  ChevronDown, ChevronUp, Lock, Settings, Play, Building2,
  Briefcase, Save, Loader2, FileText, Shield, Heart, Lightbulb, Zap,
  Star, MessageSquare, HelpCircle, Gauge, Archive, Eye, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import logger from '../utils/logger';


const IPM_COLORS = { ALTO: '#22c55e', MODERADO: '#eab308', ELEVADO: '#f97316', CRITICO: '#ef4444' };

const INDEX_ICONS = {
  IBE: Heart, ICL: Users, IDE: Lightbulb, ICR: Zap,
  ILI: Star, ICI: Shield, IOP: Gauge, ICO: MessageSquare
};

const t_i18n = {
  es: {
    title: 'Pulso MEVIA',
    results: 'Resultados',
    config: 'Configuración',
    selectPeriod: 'Seleccionar período',
    noPeriods: 'No hay períodos disponibles. Lanza tu primera encuesta desde Configuración.',
    ipmScore: 'IPM empresa',
    ipmNote: 'A menor IPM, mejor activación. Mide la brecha entre lo que el empleado necesita y lo que recibe.',
    ipmUp: '↑ {n} puntos vs período anterior',
    ipmDown: '↓ {n} puntos vs período anterior',
    participation: 'Participación',
    participationNote: 'Resultados con menos de 5 respuestas no son estadísticamente representativos. Se recomienda alcanzar al menos 70% de participación.',
    trend: 'Tendencia (últimos 6 períodos)',
    trendNote: 'Barra más corta = mejor activación. Naranja = período actual.',
    thematicTitle: 'Índices por categoría — ¿qué está pasando en tu equipo?',
    thematicSubtitle: 'Cada índice agrupa dimensiones relacionadas. Valor bajo = buena activación. Valor alto = brecha entre lo que el empleado necesita y lo que recibe.',
    dimensionDetail: 'Detalle por dimensión',
    showDetail: 'ver detalle',
    hideDetail: 'ocultar',
    iadNote: 'IAD = Índice de Activación de Dimensión. Mide la brecha entre la necesidad declarada del empleado y la activación reportada en este pulso.',
    topGaps: 'Top 3 Dimensiones con brecha',
    topBest: 'Top 3 Mejor activadas',
    ipmByDept: 'IPM por departamento',
    ipmByDeptNote: 'Identifica qué áreas tienen mayor brecha de activación. Útil para priorizar intervenciones focalizadas.',
    byRole: 'Por Puesto',
    employees: 'empleados',
    atRisk: 'Empleados en Riesgo',
    atRiskDesc: 'Empleados con IPM > 70 (Crítico)',
    freeText: 'Texto libre',
    freeTextDesc: 'Pregunta: "Si pudieras cambiar una cosa de tu trabajo, ¿qué sería?" Las respuestas se muestran de forma anónima cuando hay 5 o más participantes.',
    freeTextLocked: 'Mínimo 5 respuestas requeridas',
    freeTextLockedDesc: 'Actualmente {n} de 5. Las respuestas se desbloquean automáticamente al alcanzar el umbral para proteger el anonimato.',
    analysisTitle: 'Análisis del período',
    analysisSubtitle: 'Generado automáticamente con base en los resultados del pulso. Con más períodos acumulados, el análisis incorporará tendencias y comparativas históricas.',
    findings: 'Hallazgos principales',
    strengths: 'Fortalezas',
    opportunities: 'Áreas de oportunidad',
    conclusion: 'Conclusión y recomendación',
    periodHistory: 'Historial de períodos',
    periodHistoryDesc: 'Todos los pulsos de esta empresa. Selecciona uno arriba para ver sus resultados.',
    responses: 'respuestas',
    of: 'de',
    close: 'Cerrar',
    viewResults: 'Ver resultados',
    archive: 'Archivar',
    closeSurvey: 'Cerrar período',
    closeConfirm: '¿Cerrar este período? Se calcularán los resultados.',
    calculate: 'Calcular resultados',
    noData: 'Sin datos',
    ACTIVE: 'ACTIVO', CLOSED: 'CERRADO', ARCHIVED: 'ARCHIVADO',
    // Config
    configTitle: 'Configuración de Pulso',
    cadencia: 'Frecuencia',
    diaEnvio: 'Día de envío',
    anonimaDefault: 'Encuestas anónimas por defecto',
    weekly: 'Semanal', biweekly: 'Quincenal', monthly: 'Mensual', none: 'Sin frecuencia',
    monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves',
    friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo',
    on: 'Activado', off: 'Desactivado',
    saveConfig: 'Guardar',
    configSaved: 'Configuración guardada',
    whatMeasures: '¿Qué mide el Pulso MEVIA?',
    whatMeasuresDesc: '12 preguntas fijas, una por cada Dimensión cultural. Cada empleado responde en 3–4 minutos. El resultado es el IPM — un índice personalizado que mide qué tanto se están activando las Dimensiones que ese empleado necesita. De una sola encuesta se derivan 8 índices temáticos:',
    launchTitle: 'Lanzar encuesta manual',
    launchDesc: 'Envía el Pulso MEVIA ahora a todos los empleados activos, independientemente de la cadencia programada. Útil para aplicar tras un cambio organizacional o evento relevante.',
    launchBtn: 'Lanzar',
    launchConfirm: '¿Lanzar una encuesta de pulso manualmente a todos los empleados activos?',
    launchSuccess: 'Encuesta lanzada exitosamente',
    questionsTitle: 'Las 12 preguntas que recibirán tus empleados',
  },
  en: {
    title: 'MEVIA Pulse',
    results: 'Results',
    config: 'Settings',
    selectPeriod: 'Select period',
    noPeriods: 'No periods available. Launch your first survey from Settings.',
    ipmScore: 'Company IPM',
    ipmNote: 'A lower IPM means better activation. Measures the gap between what employees need and what they receive.',
    ipmUp: '↑ {n} points vs previous period',
    ipmDown: '↓ {n} points vs previous period',
    participation: 'Participation',
    participationNote: 'Results with fewer than 5 responses are not statistically representative. Reaching at least 70% participation is recommended.',
    trend: 'Trend (last 6 periods)',
    trendNote: 'Shorter bar = better activation. Orange = current period.',
    thematicTitle: 'Category indexes — what\'s happening in your team?',
    thematicSubtitle: 'Each index groups related dimensions. Low value = good activation. High value = gap between what the employee needs and what they receive.',
    dimensionDetail: 'Dimension detail',
    showDetail: 'show detail',
    hideDetail: 'hide',
    iadNote: 'IAD = Dimension Activation Index. Measures the gap between the employee\'s declared need and the activation reported in this pulse.',
    topGaps: 'Top 3 Dimensions with gap',
    topBest: 'Top 3 Best activated',
    ipmByDept: 'IPM by department',
    ipmByDeptNote: 'Identifies which areas have the highest activation gap. Useful for prioritizing targeted interventions.',
    byRole: 'By Role',
    employees: 'employees',
    atRisk: 'At-Risk Employees',
    atRiskDesc: 'Employees with IPM > 70 (Critical)',
    freeText: 'Free text',
    freeTextDesc: 'Question: "If you could change one thing about your work, what would it be?" Responses are shown anonymously when there are 5 or more participants.',
    freeTextLocked: 'Minimum 5 responses required',
    freeTextLockedDesc: 'Currently {n} of 5. Responses unlock automatically once the threshold is reached to protect anonymity.',
    analysisTitle: 'Period analysis',
    analysisSubtitle: 'Automatically generated based on pulse results. As more periods accumulate, the analysis will incorporate trends and historical comparisons.',
    findings: 'Key findings',
    strengths: 'Strengths',
    opportunities: 'Areas of opportunity',
    conclusion: 'Conclusion and recommendation',
    periodHistory: 'Period history',
    periodHistoryDesc: 'All surveys for this company. Select one above to view its results.',
    responses: 'responses',
    of: 'of',
    close: 'Close',
    viewResults: 'View results',
    archive: 'Archive',
    closeSurvey: 'Close period',
    closeConfirm: 'Close this period? Results will be calculated.',
    calculate: 'Calculate results',
    noData: 'No data',
    ACTIVE: 'ACTIVE', CLOSED: 'CLOSED', ARCHIVED: 'ARCHIVED',
    configTitle: 'Pulse Configuration',
    cadencia: 'Frequency',
    diaEnvio: 'Send day',
    anonimaDefault: 'Anonymous surveys by default',
    weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly', none: 'No frequency',
    monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
    friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
    on: 'On', off: 'Off',
    saveConfig: 'Save',
    configSaved: 'Configuration saved',
    whatMeasures: 'What does the MEVIA Pulse measure?',
    whatMeasuresDesc: '12 fixed questions, one per cultural Dimension. Each employee responds in 3–4 minutes. The result is the IPM — a personalized index that measures how well the Dimensions each employee needs are being activated. One survey generates 8 thematic indexes:',
    launchTitle: 'Launch manual survey',
    launchDesc: 'Sends the MEVIA Pulse now to all active employees, regardless of the scheduled cadence. Useful after an organizational change or relevant event.',
    launchBtn: 'Launch',
    launchConfirm: 'Launch a manual pulse survey to all active employees?',
    launchSuccess: 'Survey launched successfully',
    questionsTitle: 'The 12 questions your employees will receive',
  }
};

const PulsoDashboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = t_i18n[language] || t_i18n.es;

  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [freeText, setFreeText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState(null);
  const [thematicIndices, setThematicIndices] = useState(null);
  const [config, setConfig] = useState({ cadencia: 'monthly', dia_envio: 1, anonima_default: true, indices: [], questions: [] });
  const [savingConfig, setSavingConfig] = useState(false);
  const [launchingManual, setLaunchingManual] = useState(false);
  const [expandedDims, setExpandedDims] = useState(false);

  const empresaId = user?.empresa_id;
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => { if (empresaId) { loadPeriods(); loadConfig(); } }, [empresaId, language]);
  useEffect(() => { if (selectedPeriodId && empresaId) { loadPeriodResults(selectedPeriodId); loadSegments(selectedPeriodId); loadThematicIndices(selectedPeriodId); } }, [selectedPeriodId, language]);

  const loadPeriods = async () => {
    try { setLoading(true); const res = await axios.get(`/v5/pulso/${empresaId}/m13/periods?lang=${language}`); if (res.data.success && res.data.data?.length > 0) { setPeriods(res.data.data); setSelectedPeriodId(res.data.data[0].id); } } catch (e) { logger.error(e); } finally { setLoading(false); }
  };
  const loadPeriodResults = async (sid) => {
    try { const r = await axios.get(`/v5/pulso/${empresaId}/m13/results/${sid}?lang=${language}`); setPeriodData(r.data.success ? r.data.data : null); } catch { setPeriodData(null); }
    try { const f = await axios.get(`/v5/pulso/${empresaId}/m13/free-text/${sid}?lang=${language}`); if (f.data.success) setFreeText(f.data.data); } catch { /* ok */ }
  };
  const loadConfig = async () => { try { const r = await axios.get(`/v5/pulso/${empresaId}/config?lang=${language}`); if (r.data.success) setConfig(p => ({ ...p, ...r.data.data })); } catch { /* ok */ } };
  const saveConfig = async () => { try { setSavingConfig(true); await axios.put(`/v5/pulso/${empresaId}/config`, { cadencia: config.cadencia, dia_envio: config.dia_envio, anonima_default: config.anonima_default }); toast.success(t.configSaved); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSavingConfig(false); } };
  const launchManual = async () => { if (!window.confirm(t.launchConfirm)) return; try { setLaunchingManual(true); const r = await axios.post(`/v5/pulso/${empresaId}/launch-manual`); if (r.data.success) { toast.success(t.launchSuccess); loadPeriods(); } } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setLaunchingManual(false); } };
  const loadSegments = async (sid) => { try { const r = await axios.get(`/v5/pulso/${empresaId}/m13/segments/${sid}?lang=${language}`); if (r.data.success) setSegments(r.data.data); } catch { setSegments(null); } };
  const loadThematicIndices = async (sid) => { try { const r = await axios.get(`/v5/pulso/${empresaId}/m13/indices/${sid}?lang=${language}`); if (r.data.success) setThematicIndices(r.data.data); } catch { setThematicIndices(null); } };
  const handleCalculate = async () => { try { await axios.post(`/v5/pulso/${empresaId}/m13/calculate/${selectedPeriodId}`); toast.success(language === 'es' ? 'Resultados calculados' : 'Results calculated'); loadPeriodResults(selectedPeriodId); loadThematicIndices(selectedPeriodId); } catch { toast.error('Error'); } };
  const handleClose = async () => { if (!window.confirm(t.closeConfirm)) return; try { await axios.post(`/v5/pulso/${empresaId}/m13/close/${selectedPeriodId}`); toast.success(language === 'es' ? 'Período cerrado' : 'Period closed'); loadPeriods(); } catch { toast.error('Error'); } };

  const sp = periods.find(p => p.id === selectedPeriodId);
  const DAY_LABELS = [t.monday, t.tuesday, t.wednesday, t.thursday, t.friday, t.saturday, t.sunday];

  // ── Results Tab ────────────
  const ResultsTab = () => {
    if (!periodData) return <div className="py-12 text-center text-gray-400">{t.noData}</div>;
    const dims = periodData.dimensions || [];
    const part = periodData.participation;

    return (
      <div className="space-y-5">
        {/* S2 — KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* IPM */}
          <Card data-testid="pulso-ipm-card">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500">{t.ipmScore}</p>
                  <p className="text-4xl font-bold mt-1" style={{ color: IPM_COLORS[periodData.ipm_level] }}>{periodData.ipm}</p>
                  <Badge className="mt-1.5 text-xs" style={{ backgroundColor: IPM_COLORS[periodData.ipm_level], color: '#fff' }}>{periodData.ipm_level}</Badge>
                </div>
                {periodData.delta !== null && periodData.delta !== undefined && (
                  <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${periodData.delta < 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {periodData.delta < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                    {(periodData.delta < 0 ? t.ipmDown : t.ipmUp).replace('{n}', Math.abs(periodData.delta))}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">{t.ipmNote}</p>
            </CardContent>
          </Card>

          {/* Participation */}
          <Card data-testid="pulso-participation-card">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs font-medium text-gray-500">{t.participation}</p>
              <div className="flex items-end gap-2 mt-1">
                <p className="text-4xl font-bold text-teal-600">{part?.rate?.toFixed(0)}%</p>
                <p className="text-sm text-gray-400 pb-1">{part?.responded} {t.of} {part?.targeted}</p>
              </div>
              <div className="mt-2 w-full bg-gray-100 rounded-full h-2.5 relative">
                <div className="h-2.5 rounded-full bg-teal-500 transition-all" style={{ width: `${Math.min(part?.rate || 0, 100)}%` }} />
                <div className="absolute top-0 left-[70%] w-0.5 h-2.5 bg-gray-300" title="70%" />
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5 leading-relaxed">{t.participationNote}</p>
            </CardContent>
          </Card>

          {/* Trend */}
          <Card data-testid="pulso-trend-card">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs font-medium text-gray-500">{t.trend}</p>
              {periodData.trend?.length > 0 ? (
                <div className="flex items-end gap-1 mt-3 h-16">
                  {periodData.trend.slice(-6).map((pt, i, arr) => {
                    const maxIpm = Math.max(...arr.map(p => p.ipm), 100);
                    const h = Math.max((pt.ipm / maxIpm) * 100, 10);
                    const isCurrent = i === arr.length - 1;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${pt.name}: ${pt.ipm}`}>
                        <span className="text-[9px] text-gray-400 font-medium">{pt.ipm}</span>
                        <div className="w-full rounded-t" style={{ height: `${h}%`, backgroundColor: isCurrent ? '#f97316' : (IPM_COLORS[pt.level] || '#94a3b8'), minHeight: '6px' }} />
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-gray-400 text-xs mt-4">{t.noData}</p>}
              <p className="text-[10px] text-gray-400 mt-1.5">{t.trendNote}</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        {isAdmin && sp?.estado === 'ACTIVE' && (
          <div className="flex gap-3">
            <Button variant="outline" size="sm" onClick={handleCalculate} data-testid="pulso-calculate-btn"><BarChart3 className="w-4 h-4 mr-1.5" /> {t.calculate}</Button>
            <Button variant="destructive" size="sm" onClick={handleClose} data-testid="pulso-close-btn">{t.closeSurvey}</Button>
          </div>
        )}

        {/* S3 — 8 Thematic Indices */}
        {thematicIndices?.has_data && (
          <Card data-testid="pulso-thematic-indices">
            <CardHeader className="pb-1 pt-4 px-5">
              <CardTitle className="text-sm font-semibold text-gray-700">{t.thematicTitle}</CardTitle>
              <p className="text-[11px] text-gray-400 leading-relaxed mt-0.5">{t.thematicSubtitle}</p>
            </CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {thematicIndices.indices.map(idx => {
                  const Icon = INDEX_ICONS[idx.key] || Activity;
                  const color = idx.value !== null ? (idx.value <= 40 ? '#22c55e' : idx.value <= 65 ? '#eab308' : '#ef4444') : '#94a3b8';
                  return (
                    <div key={idx.key} className="border rounded-lg p-3 space-y-2" data-testid={`index-card-${idx.key}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Icon className="w-3.5 h-3.5" style={{ color }} />
                          <span className="text-xs font-semibold text-gray-600">{idx.key}</span>
                        </div>
                        {idx.value !== null && <span className="text-lg font-bold" style={{ color }}>{idx.value}</span>}
                      </div>
                      <p className="text-[11px] text-gray-500 font-medium">{idx.label}</p>
                      {idx.value !== null ? (
                        <>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(idx.value, 100)}%`, backgroundColor: color }} />
                          </div>
                          <p className="text-[10px] leading-snug" style={{ color }}>{idx.interpretation}</p>
                        </>
                      ) : <p className="text-xs text-gray-300">{t.noData}</p>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* S4 — Dimension Detail (collapsible) */}
        {dims.length > 0 && (
          <Card data-testid="pulso-dimension-detail">
            <CardHeader className="pb-0 pt-4 px-5">
              <button className="flex items-center justify-between w-full" onClick={() => setExpandedDims(!expandedDims)} data-testid="toggle-dimensions">
                <CardTitle className="text-sm font-semibold text-gray-700">{t.dimensionDetail}</CardTitle>
                <span className="text-xs text-teal-600 flex items-center gap-1">
                  {expandedDims ? <><ChevronUp className="w-3.5 h-3.5" /> {t.hideDetail}</> : <><ChevronDown className="w-3.5 h-3.5" /> {t.showDetail}</>}
                </span>
              </button>
            </CardHeader>
            {expandedDims && (
              <CardContent className="px-5 pb-4 pt-2 space-y-2.5">
                <p className="text-[10px] text-gray-400 italic mb-2">{t.iadNote}</p>
                {dims.map(d => (
                  <div key={d.key} className="space-y-1">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-700 font-medium">{d.label}</span>
                      <div className="flex items-center gap-2">
                        {d.delta !== null && d.delta !== undefined && <span className={`text-[10px] ${d.delta < 0 ? 'text-green-500' : 'text-red-500'}`}>{d.delta > 0 ? '+' : ''}{d.delta}</span>}
                        <Badge className="text-[10px] px-1.5" style={{ backgroundColor: IPM_COLORS[d.level], color: '#fff' }}>IAD {d.iad}</Badge>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="h-2 rounded-full transition-all duration-700" style={{ width: `${Math.min(d.iad, 100)}%`, backgroundColor: IPM_COLORS[d.level] }} />
                    </div>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Top Gaps & Best */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {periodData.top_gaps?.length > 0 && (
            <Card data-testid="pulso-top-gaps">
              <CardHeader className="pb-2 pt-4 px-5"><CardTitle className="text-sm font-semibold text-red-600">{t.topGaps}</CardTitle></CardHeader>
              <CardContent className="px-5 pb-4 space-y-2">
                {periodData.top_gaps.map((g, i) => (
                  <div key={i} className="flex justify-between items-center py-1 border-b last:border-0">
                    <span className="text-sm text-gray-700">{g.label}</span>
                    <Badge style={{ backgroundColor: '#ef4444', color: '#fff' }} className="text-xs">IAD {g.iad ?? g.iac}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {periodData.top_improved?.length > 0 && (
            <Card data-testid="pulso-top-best">
              <CardHeader className="pb-2 pt-4 px-5"><CardTitle className="text-sm font-semibold text-green-600">{t.topBest}</CardTitle></CardHeader>
              <CardContent className="px-5 pb-4 space-y-2">
                {periodData.top_improved.map((g, i) => (
                  <div key={i} className="flex justify-between items-center py-1 border-b last:border-0">
                    <span className="text-sm text-gray-700">{g.label}</span>
                    <Badge style={{ backgroundColor: '#22c55e', color: '#fff' }} className="text-xs">IAD {g.iad ?? g.iac}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* S5 — IPM by department */}
        {isAdmin && segments && (segments.departments?.length > 0 || segments.roles?.length > 0) && (
          <Card data-testid="pulso-segmentation">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Building2 className="w-4 h-4" /> {t.ipmByDept}</CardTitle>
              <p className="text-[11px] text-gray-400 mt-0.5">{t.ipmByDeptNote}</p>
            </CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {segments.departments?.length > 0 && (
                  <div className="space-y-2">
                    {segments.departments.map((d, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                        <div className="flex items-center gap-2"><span className="text-sm text-gray-700">{d.name}</span><span className="text-[10px] text-gray-400">({d.count} {t.employees})</span></div>
                        <Badge style={{ backgroundColor: IPM_COLORS[d.level], color: '#fff' }} className="text-xs">IPM {d.ipm}</Badge>
                      </div>
                    ))}
                  </div>
                )}
                {segments.roles?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> {t.byRole}</p>
                    <div className="space-y-2">
                      {segments.roles.map((r, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                          <div className="flex items-center gap-2"><span className="text-sm text-gray-700">{r.name}</span><span className="text-[10px] text-gray-400">({r.count} {t.employees})</span></div>
                          <Badge style={{ backgroundColor: IPM_COLORS[r.level], color: '#fff' }} className="text-xs">IPM {r.ipm}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* At Risk */}
        {periodData.at_risk_count > 0 && (
          <Card className="border-red-200" data-testid="pulso-at-risk">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold text-red-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {t.atRisk} ({periodData.at_risk_count})</CardTitle>
              <p className="text-[11px] text-gray-400">{t.atRiskDesc}</p>
            </CardHeader>
            <CardContent className="px-5 pb-4 divide-y">
              {periodData.at_risk.map((emp, i) => (
                <div key={i} className="flex justify-between items-center py-1.5 text-sm">
                  <div><span className="text-gray-700 font-medium">{emp.name}</span><span className="text-gray-400 text-xs ml-2">{emp.department}</span></div>
                  <Badge style={{ backgroundColor: '#ef4444', color: '#fff' }} className="text-xs">IPM {emp.ipm}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* S6 — Free Text */}
        <Card data-testid="pulso-free-text">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold text-gray-700">{t.freeText}</CardTitle>
            <p className="text-[11px] text-gray-400 leading-relaxed">{t.freeTextDesc}</p>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            {!freeText?.available ? (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <Lock className="w-5 h-5 text-gray-300 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-500">{t.freeTextLocked}</p>
                <p className="text-xs text-gray-400 mt-1">{t.freeTextLockedDesc.replace('{n}', freeText?.count || part?.responded || 0)}</p>
              </div>
            ) : freeText?.texts?.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {freeText.texts.map((txt, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 border-l-2 border-teal-400">"{txt}"</div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400 py-4 text-center">{t.noData}</p>}
          </CardContent>
        </Card>

        {/* S7 — Period Analysis */}
        {thematicIndices?.conclusion && (
          <Card className="bg-gray-50/50" data-testid="pulso-period-analysis">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2"><FileText className="w-4 h-4" /> {t.analysisTitle}</CardTitle>
              <p className="text-[11px] text-gray-400 mt-0.5">{t.analysisSubtitle}</p>
            </CardHeader>
            <CardContent className="px-5 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">{t.findings}</p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {part?.responded || 0} {t.responses} {t.of} {part?.targeted || 0} ({part?.rate?.toFixed(0)}%). IPM: {periodData.ipm} ({periodData.ipm_level}).
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-green-600 mb-1">{t.strengths}</p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {thematicIndices.indices?.filter(i => i.value !== null && i.value <= 40).map(i => i.label).join(', ') || t.noData}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-orange-600 mb-1">{t.opportunities}</p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {thematicIndices.indices?.filter(i => i.value !== null && i.value > 65).map(i => i.label).join(', ') || t.noData}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">{t.conclusion}</p>
                <p className="text-xs text-gray-500 leading-relaxed">{thematicIndices.conclusion}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* S8 — Period History */}
        {periods.length > 0 && (
          <Card data-testid="pulso-period-history">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold text-gray-700">{t.periodHistory}</CardTitle>
              <p className="text-[11px] text-gray-400">{t.periodHistoryDesc}</p>
            </CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="divide-y">
                {periods.map(p => {
                  const statusColor = p.estado === 'ACTIVE' ? '#22c55e' : p.estado === 'CLOSED' ? '#6366f1' : '#94a3b8';
                  return (
                    <div key={p.id} className="flex items-center justify-between py-2 text-sm">
                      <div className="flex items-center gap-3">
                        <Badge style={{ backgroundColor: statusColor, color: '#fff' }} className="text-[10px]">{t[p.estado] || p.estado}</Badge>
                        <span className="text-gray-700 font-medium">{p.nombre}</span>
                        {p.total_targeted > 0 && (
                          <span className="text-xs text-gray-400">{p.total_responded || 0} {t.of} {p.total_targeted} {t.responses} · {p.participation_rate ? `${p.participation_rate.toFixed(0)}%` : '0%'}</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setSelectedPeriodId(p.id)} data-testid={`view-period-${p.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> {t.viewResults}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // ── Config Tab ────────────
  const ConfigTab = () => (
    <div className="space-y-6" data-testid="pulso-config-tab">
      {/* Bloque 1 — Cadence */}
      <Card>
        <CardHeader className="pb-3 pt-4 px-5">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Settings className="w-4 h-4" /> {t.configTitle}</CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-5">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-600">{t.cadencia}</label>
            <Select value={config.cadencia || 'monthly'} onValueChange={v => setConfig(p => ({ ...p, cadencia: v }))}>
              <SelectTrigger data-testid="config-cadencia"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">{t.weekly}</SelectItem>
                <SelectItem value="biweekly">{t.biweekly}</SelectItem>
                <SelectItem value="monthly">{t.monthly}</SelectItem>
                <SelectItem value="none">{t.none}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-600">{t.diaEnvio}</label>
            <Select value={String(config.dia_envio ?? 1)} onValueChange={v => setConfig(p => ({ ...p, dia_envio: parseInt(v) }))}>
              <SelectTrigger data-testid="config-dia-envio"><SelectValue /></SelectTrigger>
              <SelectContent>{DAY_LABELS.map((l, i) => <SelectItem key={i} value={String(i)}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-600">{t.anonimaDefault}</label>
            <button data-testid="config-anonima-toggle" onClick={() => setConfig(p => ({ ...p, anonima_default: !p.anonima_default }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.anonima_default ? 'bg-teal-500' : 'bg-gray-300'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.anonima_default ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <Button onClick={saveConfig} disabled={savingConfig} className="w-full" data-testid="config-save-btn">
            {savingConfig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} {t.saveConfig}
          </Button>
        </CardContent>
      </Card>

      {/* Bloque 2 — Info Box */}
      <Card className="border-[#B5D4F4]" style={{ backgroundColor: '#E6F1FB' }} data-testid="pulso-what-measures">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-sm font-semibold text-blue-800 flex items-center gap-2"><HelpCircle className="w-4 h-4" /> {t.whatMeasures}</CardTitle>
          <p className="text-[11px] text-blue-700/70 mt-1 leading-relaxed">{t.whatMeasuresDesc}</p>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {(config.indices || []).map(idx => {
              const Icon = INDEX_ICONS[idx.key] || Activity;
              return (
                <div key={idx.key} className="bg-white/80 border border-blue-200/50 rounded-lg p-2.5" data-testid={`config-index-${idx.key}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-xs font-semibold text-gray-700">{idx.key}</span>
                  </div>
                  <p className="text-[11px] text-gray-600 font-medium leading-tight">{idx.label}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{idx.dimension_labels?.join(' + ')}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Bloque 3 — Launch + Questions */}
      <Card className="border-dashed border-teal-300" data-testid="pulso-launch-section">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Play className="w-4 h-4" /> {t.launchTitle}</CardTitle>
          <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">{t.launchDesc}</p>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <Button variant="outline" onClick={launchManual} disabled={launchingManual} className="w-full" data-testid="launch-manual-btn">
            {launchingManual ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />} {t.launchBtn}
          </Button>
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">{t.questionsTitle}</p>
            <div className="space-y-1.5">
              {(config.questions || []).map(q => (
                <div key={q.orden} className="flex items-start gap-2 py-1.5 border-b last:border-0 text-xs">
                  <span className="text-gray-400 w-5 flex-shrink-0">{q.orden}.</span>
                  <span className="text-gray-700 flex-1">{q.text}</span>
                  <Badge variant="outline" className="text-[9px] shrink-0">{q.tipo}</Badge>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center py-12" data-testid="pulso-loading"><Loader2 className="h-6 w-6 animate-spin text-teal-500 mr-2" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="pulso-dashboard">
      <Tabs defaultValue="results">
        <TabsList className="grid grid-cols-2 w-60">
          <TabsTrigger value="results" data-testid="pulso-tab-results">{t.results}</TabsTrigger>
          <TabsTrigger value="config" data-testid="pulso-tab-config">{t.config}</TabsTrigger>
        </TabsList>

        <TabsContent value="results" className="mt-4">
          {periods.length > 0 ? (
            <>
              <div className="mb-4">
                <Select value={selectedPeriodId} onValueChange={v => setSelectedPeriodId(v)}>
                  <SelectTrigger className="w-80" data-testid="period-selector"><SelectValue placeholder={t.selectPeriod} /></SelectTrigger>
                  <SelectContent>
                    {periods.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nombre} — <span className={p.estado === 'ACTIVE' ? 'text-green-600' : 'text-gray-500'}>{t[p.estado] || p.estado}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedPeriodId && <ResultsTab />}
            </>
          ) : (
            <div className="text-center py-12 text-gray-400" data-testid="no-periods">
              <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>{t.noPeriods}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="config" className="mt-4"><ConfigTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default PulsoDashboard;
